import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Wifi } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { ensureNamespaceLoaded } from "../../../i18n/lazyNamespaces";
import { MAX_PLATFORM_PROBE_SOCKETS } from "../../../hooks/useWorkspaceProbeSockets";
import { presenceCardSurface } from "../../../utils/teamPresence";
import { getTeamPresenceScreenTitle } from "../../../utils/teamPresenceScreenTitle";
import TeamPresenceTabBreakdown from "../../../components/TeamPresenceTabBreakdown";
import { BusyButton } from "./shared";
import { initialsFromName } from "./usePlatformConsoleOnlinePresence";

const PlatformWorkspacesOnlineTabPanel = ({
  presence,
  introKey = "platformPage.workspacesOnlineIntro",
  metaKey = "platformPage.workspacesOnlineMeta",
  emptyKey = "platformPage.workspacesOnlineEmpty",
  /** i18n (namespace app) para alerta de conexão ao vivo indisponível — default mensagem técnica da plataforma. */
  disconnectedAlertKey = "platformPage.onlineMainSocketDisconnected",
  /** i18n para “carregando demais workspaces” — default plataforma. */
  connectingPresenceKey = "platformPage.onlineSocketsConnecting",
  /** i18n para limite de workspaces monitorados — default plataforma. */
  workspaceCapAlertKey = "platformPage.onlineWorkspaceCap",
  /** Quando true, não exibe o botão Atualizar no corpo (ex.: portal revenda — usa o botão global do shell). */
  hideToolbarRefresh = false,
  /** Oculta a linha de meta agregada (quando o resumo já aparece fora, ex.: chips no portal revenda). */
  hideMetaLine = false,
}) => {
  const { t } = useTranslation("app");
  const { t: tLive } = useTranslation("settings");
  const theme = useTheme();
  const [expanded, setExpanded] = useState(() => new Set());

  const [livePresenceTickAt, setLivePresenceTickAt] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setLivePresenceTickAt(Date.now()), 25000);
    return () => clearInterval(id);
  }, []);

  const screenTitleForPath = useCallback((path) => getTeamPresenceScreenTitle(path, t), [t]);

  const {
    loadingDominios,
    dominiosError,
    workspaceHostList,
    truncatedTotal,
    mainSocketConnected,
    socketStats,
    hostsProbed,
    probesAllConnected,
    probeWorkspaceHosts,
    workspacesOnline,
    onlineItems,
    refreshList,
  } = presence;

  const onlineCardSurface = useMemo(() => presenceCardSurface(theme, "online"), [theme]);

  useEffect(() => {
    ensureNamespaceLoaded("settings");
  }, []);

  const toggle = (host) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(host)) next.delete(host);
      else next.add(host);
      return next;
    });
  };

  if (loadingDominios && !workspaceHostList.length && !dominiosError) {
    return (
      <Box sx={{ py: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (dominiosError) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography color="error">{dominiosError}</Typography>
        <BusyButton sx={{ mt: 2 }} variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={refreshList}>
          {t("billingPage.refresh")}
        </BusyButton>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} flexWrap="wrap" useFlexGap>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 220, lineHeight: 1.55 }}>
          {t(introKey)}
        </Typography>
        {!hideToolbarRefresh ? (
          <BusyButton
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={refreshList}
            loading={loadingDominios}
            loadingText={t("billingPage.refresh")}
          >
            {t("billingPage.refresh")}
          </BusyButton>
        ) : null}
      </Stack>

      {!mainSocketConnected ? (
        <Alert severity="warning">{t(disconnectedAlertKey)}</Alert>
      ) : null}

      {truncatedTotal ? (
        <Alert severity="info">{t(workspaceCapAlertKey, { max: MAX_PLATFORM_PROBE_SOCKETS })}</Alert>
      ) : null}

      {!hideMetaLine ? (
        <Typography variant="caption" color="text.secondary" display="block">
          {t(metaKey, {
            workspacesWithUsers: workspacesOnline.length,
            users: onlineItems.length,
            hosts: workspaceHostList.length,
            probes: hostsProbed.length,
            connected: socketStats.connected,
            total: socketStats.total,
          })}
        </Typography>
      ) : null}

      {probeWorkspaceHosts.length > 0 && !probesAllConnected ? (
        <Typography variant="caption" color="text.secondary">
          {t(connectingPresenceKey)}
        </Typography>
      ) : null}

      {!workspacesOnline.length ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography color="text.secondary">{t(emptyKey)}</Typography>
        </Paper>
      ) : (
        <Stack spacing={0.5}>
          {workspacesOnline.map((ws) => {
            const isOpen = expanded.has(ws.workspace_host);
            return (
              <Accordion
                key={ws.workspace_host}
                expanded={isOpen}
                onChange={() => toggle(ws.workspace_host)}
                disableGutters
                elevation={0}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  "&:before": { display: "none" },
                  bgcolor: "background.paper",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    px: 1.5,
                    minHeight: 52,
                    "& .MuiAccordionSummary-content": { my: 1, alignItems: "center", gap: 1 },
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1, minWidth: 0 }} noWrap title={ws.workspace_host}>
                    {ws.workspace_host}
                  </Typography>
                  <Chip
                    size="small"
                    label={t("platformPage.workspacesOnlineUserCount", { count: ws.count })}
                    color="success"
                    variant="outlined"
                    sx={{ fontWeight: 700, flexShrink: 0 }}
                  />
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5 }}>
                  <Stack spacing={1}>
                    {ws.users.map((row) => {
                      const tabs = row.tabs > 0 ? row.tabs : 0;
                      return (
                        <Paper
                          key={row.key}
                          variant="outlined"
                          sx={{
                            p: 1.25,
                            borderLeftWidth: 3,
                            borderLeftStyle: "solid",
                            borderLeftColor: onlineCardSurface.accent,
                            bgcolor: onlineCardSurface.bgcolor,
                          }}
                        >
                          <Stack direction="row" spacing={1.25} alignItems="center">
                            <Avatar
                              sx={{
                                width: 40,
                                height: 40,
                                fontSize: "0.85rem",
                                fontWeight: 700,
                                border: 2,
                                borderColor: onlineCardSurface.accent,
                                color: "text.primary",
                                bgcolor: (th) => alpha(th.palette.background.paper, 0.85),
                                flexShrink: 0,
                              }}
                            >
                              {initialsFromName(row.nome)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600} noWrap title={row.nome}>
                                {row.nome || "—"}
                              </Typography>
                              <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.5 }}>
                                <Tooltip title={tLive("dashboardPage.live.onlinePresenceTooltip")}>
                                  <Chip
                                    size="small"
                                    icon={<Wifi sx={{ "&&": { fontSize: 14 } }} />}
                                    label={tLive("dashboardPage.live.onlineNow")}
                                    color="success"
                                    variant="outlined"
                                    sx={{ height: 26, "& .MuiChip-label": { py: 0 } }}
                                  />
                                </Tooltip>
                              </Stack>
                              <Box sx={{ mt: 0.75 }}>
                                <TeamPresenceTabBreakdown
                                  show={tabs > 0}
                                  socketTabs={tabs}
                                  presenceDetail={row.presence_detail}
                                  tLive={tLive}
                                  screenTitleForPath={screenTitleForPath}
                                  livePresenceNowMs={livePresenceTickAt}
                                  density="dense"
                                />
                              </Box>
                            </Box>
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

export default PlatformWorkspacesOnlineTabPanel;
